import React, { useCallback, useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import api from "../../../services/apiService";
import DataTable, { createActionsColumn, DataTableButton } from "../../../components/data-table";
import AlertNotification from "../../../components/notification";
import DeleteModal from "../../../components/models/deleteModal.jsx";
import HrLocationModal from "./HrLocationModal";

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return { Authorization: token ? `Bearer ${token}` : undefined };
};

const BADGE = {
  true:  "inline-flex items-center rounded-full bg-green-50 border border-green-200 px-2.5 py-0.5 text-xs font-semibold text-green-700",
  false: "inline-flex items-center rounded-full bg-gray-100 border border-gray-200 px-2.5 py-0.5 text-xs font-semibold text-gray-500",
};

function HrLocations() {
  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  const [showModal, setShowModal]     = useState(false);
  const [editing, setEditing]         = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting]     = useState(false);

  const fetchLocations = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await api.get("/hr/locations", { headers: authHeaders() });
      setLocations(Array.isArray(res.data) ? res.data : []);
    } catch {
      setNotification({ text: "Failed to load locations.", status: false });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchLocations(); }, [fetchLocations]);

  const openCreate = () => { setEditing(null); setSubmitError(""); setShowModal(true); };
  const openEdit   = (loc) => { setEditing(loc); setSubmitError(""); setShowModal(true); };
  const closeModal = () => { if (isSubmitting) return; setShowModal(false); setEditing(null); };

  const handleSubmit = async (form) => {
    setIsSubmitting(true);
    setSubmitError("");
    try {
      if (editing) {
        await api.put(`/hr/locations/${editing.id}`, form, { headers: authHeaders() });
        setNotification({ text: "Location updated.", status: true });
      } else {
        await api.post("/hr/locations", form, { headers: authHeaders() });
        setNotification({ text: "Location added.", status: true });
      }
      setShowModal(false);
      fetchLocations();
    } catch (err) {
      setSubmitError(err?.response?.data?.message || "Failed to save location.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await api.delete(`/hr/locations/${deleteTarget.id}`, { headers: authHeaders() });
      setNotification({ text: "Location deleted.", status: true });
      setDeleteTarget(null);
      fetchLocations();
    } catch {
      setNotification({ text: "Failed to delete location.", status: false });
    } finally {
      setIsDeleting(false);
    }
  };

  const columns = [
    { key: "name", label: "Name", sortable: true, searchable: true,
      render: (v) => <span className="font-medium text-app-secondary">{v}</span> },
    { key: "city", label: "City", sortable: true, searchable: true, render: (v) => v || "—" },
    { key: "country", label: "Country", sortable: true, searchable: true, render: (v) => v || "—" },
    { key: "address", label: "Address", searchable: true,
      render: (v) => <span className="block max-w-[220px] truncate" title={v || ""}>{v || "—"}</span> },
    { key: "isActive", label: "Status", sortable: true,
      render: (v) => <span className={BADGE[String(Boolean(v))]}>{v ? "Active" : "Inactive"}</span> },
    createActionsColumn((loc) => [
      { label: "Edit", icon: Pencil, onSelect: () => openEdit(loc) },
      { label: "Delete", icon: Trash2, tone: "danger", onSelect: () => setDeleteTarget(loc) },
    ], { getRowLabel: (loc) => loc.name }),
  ];

  return (
    <div className="space-y-5">
      <p className="text-sm text-app-muted">Manage office locations for employees and assets.</p>

      <DataTable
        ariaLabel="Locations"
        columns={columns}
        data={locations}
        loading={isLoading}
        searchable
        searchPlaceholder="Search name, city, country or address"
        toolbarRight={
          <DataTableButton variant="primary" icon={Plus} onClick={openCreate}>
            Add Location
          </DataTableButton>
        }
        pagination
        defaultPageSize={10}
        labels={{ empty: "No locations found." }}
      />

      {notification && <AlertNotification text={notification.text} status={notification.status} onClose={() => setNotification(null)} />}

      <HrLocationModal
        key={editing?.id ?? "new"}
        show={showModal}
        onClose={closeModal}
        onSubmit={handleSubmit}
        initialData={editing}
        isSubmitting={isSubmitting}
        error={submitError}
      />

      <DeleteModal
        show={Boolean(deleteTarget)}
        handleClose={() => { if (!isDeleting) setDeleteTarget(null); }}
        handleConfirm={handleDelete}
        message={`Delete location "${deleteTarget?.name}"? This cannot be undone.`}
        loading={isDeleting}
      />
    </div>
  );
}

export default HrLocations;
